import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { AdminService } from '../../services/AdminService';
import { ProductService } from '../../services/ProductService';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  useTheme,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  People as PeopleIcon,
  Inventory as InventoryIcon,
  ShoppingCart as OrdersIcon,
  AttachMoney as RevenueIcon,
  Refresh as RefreshIcon,
  Update as UpdateIcon,
  CalendarViewDay,
  CalendarViewMonth
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { useSnackbar } from 'notistack';

export default function Dashboard() {
  const theme = useTheme();
  const [refreshKey, setRefreshKey] = useState(0);
  const { enqueueSnackbar } = useSnackbar();
  const [pendingProducts, setPendingProducts] = useState([]);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const queryClient = useQueryClient();
  const [revenueView, setRevenueView] = useState('daily');

  const { data: stats, isLoading, error } = useQuery(
    ['adminStats', refreshKey],
    () => AdminService.getDashboardStats(),
    {
      refetchInterval: 300000 // Refresh every 5 minutes
    }
  );

  const { data: pendingProductsData, refetch: refetchPending } = useQuery(
    'pendingProducts',
    () => AdminService.getPendingProducts()
  );

  const approveMutation = useMutation(
    (productId) => AdminService.approveProduct(productId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('pendingProducts');
        enqueueSnackbar('Product approved successfully', { variant: 'success' });
      }
    }
  );

  const rejectMutation = useMutation(
    ({ productId, reason }) => AdminService.rejectProduct(productId, reason),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('pendingProducts');
        setRejectDialog(false);
        setRejectionReason('');
        enqueueSnackbar('Product rejected', { variant: 'success' });
      }
    }
  );

  const COLORS = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
    theme.palette.info.main
  ];

  const handleUpdateProducts = async () => {
    try {
      await ProductService.updateProductsWithLowerCase();
      enqueueSnackbar('Products updated successfully', { variant: 'success' });
      // Refresh dashboard data
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      enqueueSnackbar('Failed to update products', { variant: 'error' });
    }
  };

  const handleUpdateProductsStatus = async () => {
    try {
      const count = await AdminService.updateExistingProductsStatus();
      enqueueSnackbar(`Updated ${count} products with status`, { variant: 'success' });
      queryClient.invalidateQueries('pendingProducts');
    } catch (error) {
      enqueueSnackbar('Failed to update products status', { variant: 'error' });
    }
  };

  const getRevenueData = () => {
    const revenueData = {};
    
    stats?.recentOrders?.forEach(order => {
      if (order.createdAt) {
        const date = order.createdAt.toDate();
        let key;
        
        if (revenueView === 'daily') {
          key = date.toLocaleDateString(); // Format: MM/DD/YYYY
        } else {
          key = `${date.getMonth() + 1}/${date.getFullYear()}`; // Format: MM/YYYY
        }
        
        revenueData[key] = (revenueData[key] || 0) + (order.total || 0);
      }
    });

    // Convert to array and sort by date
    return Object.entries(revenueData)
      .map(([date, revenue]) => ({
        date,
        revenue
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const handleRevenueViewChange = (event, newView) => {
    if (newView !== null) {
      setRevenueView(newView);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error.message}
      </Alert>
    );
  }

  const monthlyRevenueData = Object.entries(stats.monthlyRevenue || {})
    .map(([month, revenue]) => ({
      month,
      revenue
    }))
    .sort((a, b) => {
      const [aMonth, aYear] = a.month.split('/');
      const [bMonth, bYear] = b.month.split('/');
      return new Date(aYear, aMonth - 1) - new Date(bYear, bMonth - 1);
    });

  const categoryData = Object.entries(stats?.salesByCategory || {})
    .map(([category, sales]) => ({
      name: category,
      value: sales
    }))
    .filter(item => item.value > 0);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
        <Typography variant="h4">Dashboard</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={async () => {
              try {
                const count = await ProductService.updateExistingProductsSearchFields();
                enqueueSnackbar(`Updated ${count} products with search fields`, { 
                  variant: 'success' 
                });
                // Refresh dashboard data
                queryClient.invalidateQueries('products');
              } catch (error) {
                enqueueSnackbar('Failed to update products', { 
                  variant: 'error' 
                });
              }
            }}
            startIcon={<UpdateIcon />}
          >
            Update Search Fields
          </Button>
          <Button
            variant="outlined"
            onClick={handleUpdateProductsStatus}
            startIcon={<UpdateIcon />}
          >
            Update Products Status
          </Button>
          <IconButton onClick={() => setRefreshKey(k => k + 1)}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PeopleIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Total Users</Typography>
              </Box>
              <Typography variant="h4">{stats.totalUsers}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <InventoryIcon color="secondary" sx={{ mr: 1 }} />
                <Typography variant="h6">Products</Typography>
              </Box>
              <Typography variant="h4">{stats.totalProducts}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <OrdersIcon color="success" sx={{ mr: 1 }} />
                <Typography variant="h6">Orders</Typography>
              </Box>
              <Typography variant="h4">{stats.totalOrders}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <RevenueIcon color="warning" sx={{ mr: 1 }} />
                <Typography variant="h6">Revenue</Typography>
              </Box>
              <Typography variant="h4">${stats.totalRevenue.toFixed(2)}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Row */}
        {/* Revenue Chart */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3
            }}>
              <Typography variant="h6">
                {revenueView === 'daily' ? 'Daily Revenue' : 'Monthly Revenue'}
              </Typography>
              <ToggleButtonGroup
                value={revenueView}
                exclusive
                onChange={handleRevenueViewChange}
                size="small"
              >
                <ToggleButton value="daily" aria-label="daily view">
                  <CalendarViewDay sx={{ mr: 1 }} />
                  Daily
                </ToggleButton>
                <ToggleButton value="monthly" aria-label="monthly view">
                  <CalendarViewMonth sx={{ mr: 1 }} />
                  Monthly
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Box sx={{ height: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={getRevenueData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    interval={revenueView === 'daily' ? 1 : 0}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                    tickFormatter={(value) => {
                      if (revenueView === 'daily') {
                        return new Date(value).toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          year: '2-digit'
                        });
                      }
                      return value; // Keep MM/YYYY format for monthly view
                    }}
                  />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => `$${value.toFixed(0)}`}
                    width={80}
                  />
                  <Tooltip
                    formatter={(value) => [`$${value.toFixed(2)}`, 'Revenue']}
                    labelFormatter={(label) => {
                      if (revenueView === 'daily') {
                        return new Date(label).toLocaleDateString(undefined, {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        });
                      }
                      const [month, year] = label.split('/');
                      return `${new Date(year, month - 1).toLocaleString('default', { month: 'long' })} ${year}`;
                    }}
                    contentStyle={{
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      border: 'none',
                      borderRadius: 8,
                      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                      padding: '10px 14px'
                    }}
                  />
                  <Legend 
                    verticalAlign="top"
                    height={36}
                  />
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    name="Revenue"
                    stroke={theme.palette.primary.main}
                    strokeWidth={2}
                    dot={{ r: 3, strokeWidth: 2 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* ... other charts ... */}

        {/* Sales by Category Chart */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Sales by Category
            </Typography>
            <Box sx={{ height: 400 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={categoryData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={130}
                    label={({
                      cx,
                      cy,
                      midAngle,
                      innerRadius,
                      outerRadius,
                      value,
                      name
                    }) => {
                      const RADIAN = Math.PI / 180;
                      const radius = 35 + innerRadius + (outerRadius - innerRadius);
                      const x = cx + radius * Math.cos(-midAngle * RADIAN);
                      const y = cy + radius * Math.sin(-midAngle * RADIAN);

                      return (
                        <text
                          x={x}
                          y={y}
                          fill="#666"
                          textAnchor={x > cx ? 'start' : 'end'}
                          dominantBaseline="central"
                          style={{ fontSize: '0.875rem' }}
                        >
                          {`${name} (${value})`}
                        </text>
                      );
                    }}
                  >
                    {categoryData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value, name) => [`${value} sales`, name]}
                    contentStyle={{ fontSize: '0.875rem' }}
                  />
                  <Legend 
                    verticalAlign="bottom"
                    height={36}
                    formatter={(value) => (
                      <span style={{ fontSize: '0.875rem' }}>{value}</span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
        

        {/* Recent Orders and Popular Products */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recent Orders
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Order ID</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.recentOrders.map((order) => (
                    <TableRow key={order.id}>
                      <TableCell>{order.id}</TableCell>
                      <TableCell>
                        {order.createdAt?.toDate().toLocaleDateString()}
                      </TableCell>
                      <TableCell>{order.status}</TableCell>
                      <TableCell align="right">${order.total.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Popular Products
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell align="right">Views</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.popularProducts.map((product) => (
                    <TableRow key={product.id}>
                      <TableCell>{product.title}</TableCell>
                      <TableCell>{product.category}</TableCell>
                      <TableCell align="right">${product.price.toFixed(2)}</TableCell>
                      <TableCell align="right">{product.views || 0}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Product Validation Section */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Pending Products
            </Typography>
            
            {pendingProductsData?.length === 0 ? (
              <Typography color="text.secondary" align="center" py={4}>
                No products pending approval
              </Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Price</TableCell>
                      <TableCell>Seller</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingProductsData?.map((product) => (
                      <TableRow key={product.id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <img 
                              src={product.images?.[0]} 
                              alt={product.title}
                              style={{ 
                                width: 50, 
                                height: 50, 
                                objectFit: 'cover',
                                borderRadius: 4
                              }}
                            />
                            <Typography>{product.title}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{product.category}</TableCell>
                        <TableCell>${product.price}</TableCell>
                        <TableCell>{product.sellerName}</TableCell>
                        <TableCell>
                          {product.createdAt?.toDate().toLocaleDateString()}
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            color="success"
                            onClick={() => approveMutation.mutate(product.id)}
                            sx={{ mr: 1 }}
                          >
                            Approve
                          </Button>
                          <Button
                            color="error"
                            onClick={() => {
                              setSelectedProduct(product);
                              setRejectDialog(true);
                            }}
                          >
                            Reject
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Reject Dialog */}
      <Dialog 
        open={rejectDialog} 
        onClose={() => setRejectDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Product</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Are you sure you want to reject "{selectedProduct?.title}"?
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason for rejection"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog(false)}>
            Cancel
          </Button>
          <Button
            color="error"
            onClick={() => {
              rejectMutation.mutate({
                productId: selectedProduct?.id,
                reason: rejectionReason
              });
            }}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      
    </Container>
  );
} 